import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ChevronRight, Star, MapPin, UtensilsCrossed } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://images.unsplash.com/photo-1621494268492-d01b98eba7e4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920";
const LOGO = "https://customer-assets.emergentagent.com/job_culinary-connect-37/artifacts/dowryrls_582627c1-c0bf-47f6-ab36-5cc8b78702e3.png";
const DEFAULT_CHEF_PHOTO = "https://images.pexels.com/photos/30892994/pexels-photo-30892994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400";

const resolvePhoto = (url) => {
  if (!url) return DEFAULT_CHEF_PHOTO;
  return url.startsWith("/") ? `${API}${url}` : url;
};

const UK_REGIONS = [
  "London", "South East", "South West", "East of England", "East Midlands",
  "West Midlands", "Yorkshire and The Humber", "North West", "North East",
  "Scotland", "Wales", "Northern Ireland"
];
const CUISINES = [
  "British", "French", "Italian", "Spanish", "Indian", "Japanese",
  "Mediterranean", "Modern European", "Asian Fusion", "Seafood"
];

export default function HomePage() {
  const navigate = useNavigate();
  const [region, setRegion] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [date, setDate] = useState("");
  const [featuredChefs, setFeaturedChefs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/chefs?limit=3`)
      .then(r => setFeaturedChefs(r.data.chefs || []))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (region) params.set("region", region);
    if (cuisine) params.set("cuisine", cuisine);
    if (date) params.set("date", date);
    navigate(`/find-a-chef?${params.toString()}`);
  };

  return (
    <div className="bg-[#050505]">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-[#050505]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20">
          <img src={LOGO} alt="Are You A Chef" className="h-24 w-24 rounded-full mx-auto mb-8 shadow-2xl shadow-black/50" />
          <p className="text-xs tracking-[0.35em] text-[#D4AF37] uppercase mb-4">Private Chef Directory &bull; United Kingdom</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white font-light leading-tight mb-6">
            Find Your Perfect<br />
            <span className="text-[#D4AF37] italic">Private Chef</span>
          </h1>
          <p className="text-[#A0A0A0] text-base sm:text-lg mb-12 max-w-2xl mx-auto">
            Browse exceptional culinary talent across the UK. Filter by region, cuisine and availability.
          </p>

          {/* Search Form */}
          <form
            data-testid="hero-search-form"
            onSubmit={handleSearch}
            className="bg-[#0A0A0A]/90 border border-[#D4AF37]/25 p-6 sm:p-8 backdrop-blur-xl text-left"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Region</label>
                <select
                  data-testid="search-region-select"
                  value={region}
                  onChange={e => setRegion(e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors cursor-pointer"
                >
                  <option value="" className="bg-[#121212]">Any Region</option>
                  {UK_REGIONS.map(r => <option key={r} value={r} className="bg-[#121212]">{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Cuisine</label>
                <select
                  data-testid="search-cuisine-select"
                  value={cuisine}
                  onChange={e => setCuisine(e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors cursor-pointer"
                >
                  <option value="" className="bg-[#121212]">Any Cuisine</option>
                  {CUISINES.map(c => <option key={c} value={c} className="bg-[#121212]">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Available Date</label>
                <input
                  data-testid="search-date-input"
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="hero-search-btn"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Search size={16} />
              Search Chefs
            </button>
          </form>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-[#D4AF37]/10 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "12", label: "UK Regions" },
              { value: "18", label: "Cuisine Types" },
              { value: "£40", label: "Per Quarter to List" },
              { value: "100%", label: "Professional Chefs" },
            ].map(stat => (
              <div key={stat.label}>
                <div className="font-serif text-3xl text-[#D4AF37] mb-1">{stat.value}</div>
                <div className="text-xs tracking-[0.15em] uppercase text-[#A0A0A0]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Simple Process</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-light">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D4AF37]/10">
            {[
              { step: "01", title: "Search", desc: "Filter by UK region, cuisine type and date availability to find your ideal private chef." },
              { step: "02", title: "Browse Profiles", desc: "Explore detailed chef profiles including menus, services, experience and client reviews." },
              { step: "03", title: "Book Directly", desc: "Contact your chosen chef directly to discuss your event requirements and secure a date." },
            ].map(item => (
              <div key={item.step} className="bg-[#050505] p-8 lg:p-12">
                <div className="font-serif text-5xl text-[#D4AF37]/20 font-light mb-4">{item.step}</div>
                <h3 className="font-serif text-2xl text-white mb-3">{item.title}</h3>
                <p className="text-[#A0A0A0] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Chefs */}
      {featuredChefs.length > 0 && (
        <section className="py-24 bg-[#080808]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Discover</p>
                <h2 className="font-serif text-4xl sm:text-5xl text-white font-light">Featured Chefs</h2>
              </div>
              <a href="/find-a-chef"
                className="hidden sm:flex items-center gap-2 text-[#D4AF37] text-sm hover:text-white transition-colors">
                View all <ChevronRight size={16} />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D4AF37]/10">
              {featuredChefs.map(chef => (
                <div key={chef._id}
                  className="bg-[#0A0A0A] hover:bg-[#111] group cursor-pointer transition-colors duration-300"
                  onClick={() => navigate(`/chef/${chef._id}`)}
                  data-testid={`featured-chef-${chef._id}`}
                >
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={resolvePhoto(chef.photo_url)}
                      alt={chef.chef_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={12} className="text-[#D4AF37]" />
                      <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">
                        {chef.regions?.[0] || "UK"}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl text-white mb-2">{chef.chef_name}</h3>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(chef.cuisine_specialties || []).slice(0, 3).map(c => (
                        <span key={c} className="text-xs px-2 py-0.5 border border-[#D4AF37]/25 text-[#A0A0A0]">{c}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      {chef.price_range_min && (
                        <span className="text-[#A0A0A0] text-sm">From £{chef.price_range_min}</span>
                      )}
                      <span className="text-[#D4AF37] text-sm ml-auto">View Profile →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* For Chefs CTA */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1603739592262-e66a6708e141?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920)`,
          backgroundSize: "cover", backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">For Professional Chefs</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light mb-6">
            List Your Services &<br />
            <span className="text-[#D4AF37] italic">Reach New Clients</span>
          </h2>
          <p className="text-[#A0A0A0] mb-4 max-w-xl mx-auto">
            Join the UK&apos;s premier private chef directory. Create your professional profile and connect with clients looking for exceptional culinary experiences.
          </p>
          <div className="inline-block border border-[#D4AF37]/30 px-6 py-3 mb-8">
            <span className="font-serif text-3xl text-[#D4AF37]">£40</span>
            <span className="text-[#A0A0A0] text-sm ml-2">per quarter &bull; cancel anytime</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/register?role=chef"
              data-testid="cta-list-services-btn"
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-10 py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-300 inline-block">
              Start Listing Today
            </a>
            <a href="/find-a-chef"
              className="border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] px-10 py-4 tracking-widest uppercase text-sm transition-colors duration-300 inline-block">
              Browse the Directory
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
